Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _defineProperty = _interopDefault(require('@babel/runtime/helpers/defineProperty'));
var _objectWithoutProperties = _interopDefault(require('@babel/runtime/helpers/objectWithoutProperties'));
var React = require('react');
var React__default = _interopDefault(React);
var PropTypes = _interopDefault(require('prop-types'));
var _regeneratorRuntime = _interopDefault(require('@babel/runtime/regenerator'));
var _asyncToGenerator = _interopDefault(require('@babel/runtime/helpers/asyncToGenerator'));
var _slicedToArray = _interopDefault(require('@babel/runtime/helpers/slicedToArray'));
var reactDom = require('react-dom');

const event = 'ReactOverridableDevMode';
function startDevMode() {
  window.dispatchEvent(new Event(event));
  window._ReactOverridableIsDevMode = true;
}
/**
 * A globally exposed function. When called, this activates the developer mode for
 * React Overridable.
 * All overridable components will show their IDs in a small overlay tag until the next page reload.
 */

window.reactOverridableEnableDevMode = startDevMode;
/**
 * Returns true if dev mode is active, false otherwise.
 */

function useDevMode() {
  var _window$_ReactOverrid;

  const _useState = React.useState((_window$_ReactOverrid = window._ReactOverridableIsDevMode) !== null && _window$_ReactOverrid !== void 0 ? _window$_ReactOverrid : false),
        _useState2 = _slicedToArray(_useState, 2),
        isDevMode = _useState2[0],
        setDevMode = _useState2[1];

  React.useEffect(() => {
    const eventHandler = () => {
      setDevMode(true);
    };

    window.addEventListener(event, eventHandler);
    return () => {
      window.removeEventListener(event, eventHandler);
    };
  }, []);
  return isDevMode;
}
let _overlayRoot = null;
/**
 * In order to avoid a full re-render of the component when devmode gets activated
 * (which can cause bugs in older components with complex side-effects) we use a top-level
 * div overlay and display the ID tags as absolute-positioned elements located at the location
 * of the relevant overridable.
 *
 * This function creates this overlay if it has not yet been created and returns a reference to it.
 */

function useOverlayRoot() {
  React.useEffect(() => {
    if (!_overlayRoot) {
      _overlayRoot = document.createElement('div');
      document.body.appendChild(_overlayRoot);
    }
  }, []);
  return _overlayRoot;
}
/**
 * Renders the child (i.e. the default component or the override) as normal, ensuring
 * there are no modifications to the DOM whatsoever when dev mode is not active.
 * This means regular rendering of the component is unaffected.
 *
 * When dev mode gets activated, we also ensure a full re-render does not happen
 * (e.g. wrapping the component in a new div). Instead, we insert a <span> just
 * before the child which acts as a location anchor. We use this to calculate where to
 * show the absolute-positioned ID tag in the top-level overlay.
 */


function DevModeWrapper({
  id,
  children
}) {
  const isDevMode = useDevMode();
  const overlayRoot = useOverlayRoot();
  const ref = React.useRef();
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, isDevMode && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("span", {
    ref: ref
  }), reactDom.createPortal( /*#__PURE__*/React__default.createElement(IDTag, {
    id: id,
    targetRef: ref
  }), overlayRoot)), children);
}
DevModeWrapper.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};
const tagPositions = {}; // To avoid having to integrate precise size measuring (which depends on OS, browser, font, etc),
// we just make some reasonable assumptions and create a bounding box of this size.

const presumedHeight = 25;
const presumedWidth = 400;

function storeTagPosition(id, top, left) {
  tagPositions[id] = {
    top,
    left,
    bottom: top + presumedHeight,
    right: left + presumedWidth
  };
}

function removeTagPosition(id) {
  delete tagPositions[id];
} // Returns true if the proposed location (with the height/width presumptions) would overlap
// with another tag for a different ID.


function overlapExists(id, refTop, refLeft) {
  const provisionalBottom = refTop + presumedHeight;
  const provisionalRight = refLeft + presumedWidth;
  return Object.entries(tagPositions).some(([thisId, t]) => {
    if (thisId === id) {
      return false;
    }

    if (provisionalRight < t.left || t.right < refLeft) {
      return false;
    }

    if (provisionalBottom < t.top || t.bottom < refTop) {
      return false;
    }

    return true;
  });
}

function getNewPosition(id, refTop, refLeft) {
  let top = refTop;

  while (overlapExists(id, top, refLeft)) {
    // Keep moving the tag location downwards until we no longer have an overlap.
    top++;
  }

  return {
    top,
    left: refLeft
  };
}

function IDTag({
  targetRef,
  id
}) {
  const _useState3 = React.useState(null),
        _useState4 = _slicedToArray(_useState3, 2),
        position = _useState4[0],
        setPosition = _useState4[1];

  const _useState5 = React.useState(false),
        _useState6 = _slicedToArray(_useState5, 2),
        copied = _useState6[0],
        setCopied = _useState6[1];

  React.useEffect(() => {
    const node = targetRef.current;
    if (!node) return;
    let frameId;

    const animationFrameHandler = () => {
      const rect = node.getBoundingClientRect();
      const parentRect = node.parentElement.getBoundingClientRect();

      if (parentRect.width === 0 || parentRect.height === 0) {
        // If width/height is 0 then the element is not visible so we should avoid showing the tag.
        setPosition(null);
      } else {
        const newPosition = getNewPosition(id, rect.top + window.scrollY, rect.left + window.scrollX);
        storeTagPosition(id, newPosition.top, newPosition.left);
        setPosition(newPosition);
      } // Poll this function constantly without affecting UI performance


      frameId = requestAnimationFrame(animationFrameHandler);
    };

    animationFrameHandler();
    return () => {
      removeTagPosition(id);
      cancelAnimationFrame(frameId);
    };
  }, [targetRef, id]);
  const onClick = React.useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _context.next = 2;
          return navigator.clipboard.writeText(id);

        case 2:
          setCopied(true);
          setTimeout(() => setCopied(false), 1000);

        case 4:
        case "end":
          return _context.stop();
      }
    }, _callee);
  })), [id]);
  if (!position) return null;
  return /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    onClick: onClick,
    style: {
      position: 'absolute',
      top: position.top,
      left: position.left,
      background: 'darkred',
      color: 'white',
      fontSize: '10px',
      zIndex: 9999,
      padding: '2px 4px',
      borderRadius: '4px',
      border: '1px solid white',
      fontWeight: 'bold',
      cursor: 'pointer'
    }
  }, copied ? 'Copied!' : id);
}

IDTag.propTypes = {
  id: PropTypes.string.isRequired,
  targetRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

const OverridableContext = React__default.createContext({});
/**
 * Function to wrap a React component and override default props.
 * @param Component the component to wrap
 * @param extraProps the new props that will override or will be added to the wrapped component
 * @returns the wrapper component
 */

function parametrize(Component, extraProps) {
  const ParametrizedComponent = props => {
    // handle deferred prop calculation
    if (typeof extraProps === 'function') {
      extraProps = extraProps(props);
    } // Store the original component in an attribute


    if (Component.originalComponent) {
      Component = Component.originalComponent;
    } // overrideProps override props if there is a name collision


    const _props$extraProps = _objectSpread(_objectSpread({}, props), extraProps),
          children = _props$extraProps.children,
          attrProps = _objectWithoutProperties(_props$extraProps, ["children"]);

    return React__default.createElement(Component, attrProps, children);
  };

  const name = Component.displayName || Component.name;
  ParametrizedComponent.displayName = "Parametrized(".concat(name, ")");
  return ParametrizedComponent;
}
/**
 * React component to enable overriding children when rendering.
 */

function Overridable(_ref) {
  let id = _ref.id,
      children = _ref.children,
      restProps = _objectWithoutProperties(_ref, ["id", "children"]);

  const overriddenComponents = React.useContext(OverridableContext);
  const child = children ? React__default.Children.only(children) : null;
  const childProps = child ? child.props : {};

  if (id in overriddenComponents) {
    // If there's an override, we replace the component's content with the override + props
    const Overridden = overriddenComponents[id];
    const element = React__default.createElement(Overridden, _objectSpread(_objectSpread({}, childProps), restProps));
    return /*#__PURE__*/React__default.createElement(DevModeWrapper, {
      id: id
    }, element);
  } else if (child) {
    // No override? Clone the Overridable component's original children
    const element = React__default.cloneElement(child, childProps);
    return /*#__PURE__*/React__default.createElement(DevModeWrapper, {
      id: id
    }, element);
  } else {
    return null;
  }
}

Overridable.propTypes = {
  /** The children of the component */
  children: PropTypes.node,

  /** The id that the component will be bound to (normally component's name) */
  id: PropTypes.string
};
Overridable.defaultProps = {
  id: null,
  children: null
};
/**
 * High-order component to override an existing React component and provide a new component instead.
 */

Overridable.component = (id, Component) => {
  const Overridden = (_ref2) => {
    let children = _ref2.children,
        props = _objectWithoutProperties(_ref2, ["children"]);

    const overriddenComponents = React.useContext(OverridableContext);
    const overriddenComponent = overriddenComponents[id];
    return React__default.createElement(overriddenComponent || Component, props, children);
  };

  Overridden.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
  };
  Overridden.defaultProps = {
    children: null
  };
  const name = Component.displayName || Component.name;
  Overridden.displayName = "Overridable(".concat(name, ")");
  Overridden.originalComponent = Component;
  return Overridden;
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Simple utility class responsible of keeping track of all overridden components.
 * @constructor object containing the initial map `id: Component` of overridden components
 */
class OverriddenComponentRepository {
  constructor(overriddenComponents) {
    _defineProperty(this, "add", (id, Component) => {
      this.components[id] = Component;
    });

    _defineProperty(this, "get", id => {
      return this.components[id];
    });

    _defineProperty(this, "getAll", () => {
      return _objectSpread$1({}, this.components);
    });

    _defineProperty(this, "clear", () => {
      this.components = {};
    });

    this.components = overriddenComponents || {};
  }

}
const overrideStore = new OverriddenComponentRepository();

exports.OverridableContext = OverridableContext;
exports.OverriddenComponentRepository = OverriddenComponentRepository;
exports.default = Overridable;
exports.overrideStore = overrideStore;
exports.parametrize = parametrize;
//# sourceMappingURL=index.js.map
